// The axis config is mainly determined by the unit plotted, so there is a grouped config per unit
import angular from "angular";
import _ from "lodash";


const socConfig = {
    min: 0,
    max: 100,
    tickInterval: 10,
    ceiling: 100,
    floor: 0
};

const dbConfig = {
    minTickInterval: 10,
    tickAmount: 10
};

const frequencyConfig = {
    min: 40,
    max: 70,
    tickInterval: 10
};

const temperatureConfig = {
    maxPadding: 0.2, // 20% percent padding
    minPadding: 0
};

const temperatureConfigWithoutMin = {
    maxPadding: 0.2, // 20% percent padding
    minPadding: 0,
    tickAmount: 10,
    minTickInterval: 2
}

const powerConfig = {
    minPadding: 0,
    minTickInterval: 1,
    maxPadding: 0.1, // 10% percent padding
    forceTickAt0: true, // Show tick at 0, and allow negative values
    tickAmount: 7
};

const solarPowerPvYield = {
    minPadding: 0,
    minTickInterval: 1,
    maxPadding: 0.1, // 10% percent padding
    forceTickAt0: true, // Show tick at 0, and allow negative values
    tickAmount: 7,
    min : 0
};

const solarIrradiance = {
    minPadding: 0,
    minTickInterval: 10,
    maxPadding: 0.1, // 10% percent padding
    forceTickAt0: true, // Show tick at 0, and allow negative values
    tickAmount: 100,
    min : 0
};

const rpmConfig = {
    forceTickAt0: true, // Show tick at 0, and allow negative values
    minPadding: 0.2,
    maxPadding: 0.2,
    tickAmount: 10
};


const flowConfig = {
    forceTickAt0: true, // Show tick at 0, and allow negative values
    minPadding: 0.2,
    maxPadding: 0.2,
    tickAmount: 10
};

const customWidgetConfig = {
    forceTickAt0: true, // Show tick at 0, and allow negative values
    minPadding: 0.1,
    maxPadding: 0.1
};

const batteryVoltageConfig = {
    tickAmount: 10,
    minTickInterval: 0.1,
    labels: {
        format: '{value:.2f}'
    },
    maxPadding: 0.2,
    minPadding: 0.2
};

const batteryCellVoltageConfig = {
    ...batteryVoltageConfig, // Base settings
    minTickInterval: 0.05, // Overwrite minTick interval, the cell voltage is much lower than that of the entire battery
};

const batteryCurrentConfig = {
    forceTickAt0: true, // Show tick at 0, and allow negative values
    tickAmount: 10,
    minPadding: 0.2,
    maxPadding: 0.2,
    labels: {
        format: '{value:.2f}'
    }
};

const percentageConfig = {
    tickAmount: 10,
    minPadding: 0.2,
    maxPadding: 0.2,
    minTickInterval: 0.2,
    className: "highcharts-label-percentage-config",
    labels: {
        format: '{value:.2f}',
    },
};

const voltageConfig = {
    tickAmount: 5,
    min: 0,
    maxPadding: 0.2
};

const voltageWithoutMinConfig = {
    tickAmount: 10,
    minTickInterval: 0.1,
    maxPadding: 0.2,
    labels: {
        format: '{value:.2f}'
    }
};

const currentConfig = {
    minPadding: 0,
    maxPadding: 0.2,
    minTickInterval: 0.01,
    forceTickAt0: true // Show tick at 0, and allow negative values
};

// This one is used in conjunction with voltageWithoutMinConfig so both axis have 10 ticks
const current10TicksConfig = {
    ...currentConfig,
    tickAmount: 10,
};

// List of data attributes that measure energy (Wh/kWh) and should be displayed as columns
const columnDataAttributes = [
    'da',
    'du',
    'dq',
    'vrm_pv_charger_yield_fc',
    'vrm_pv_inverter_yield_fc',
    'vrm_consumption_fc',
    'vrm_inverter_yield_fc',
    'vrm_inverter_yield_0_fc',
    'vrm_inverter_yield_1_fc',
    'vrm_inverter_yield_2_fc',
    'vrm_inverter_yield_3_fc',
    'vrm_inv_crg_yield_fc',
    'vrm_inv_crg_yield_0_fc',
    'vrm_inv_crg_yield_1_fc',
    'vrm_inv_crg_yield_2_fc',
    'vrm_inv_crg_yield_3_fc',
    'vrm_pv_charger_yield_0_fc',
    'vrm_pv_charger_yield_1_fc',
    'vrm_pv_charger_yield_2_fc',
    'vrm_pv_charger_yield_3_fc',
    'vrm_to_battery_fc',
    'vrm_to_grid_fc',
    'vrm_to_battery_def',
    'vrm_to_grid_def',
    'vrm_inv_crg_yield_0_fc2',
    'vrm_inv_crg_yield_1_fc2',
    'vrm_inv_crg_yield_2_fc2',
    'vrm_inv_crg_yield_3_fc2',
    'vrm_inv_crg_yield_fc2',
    'vrm_inverter_yield_0_fc2',
    'vrm_inverter_yield_1_fc2',
    'vrm_inverter_yield_2_fc2',
    'vrm_inverter_yield_3_fc2',
    'vrm_inverter_yield_fc2',
    'vrm_pv_charger_yield_fc2',
    'vrm_pv_crg_yield_0_fc2',
    'vrm_pv_crg_yield_1_fc2',
    'vrm_pv_crg_yield_2_fc2',
    'vrm_pv_crg_yield_3_fc2',
    'vrm_pv_inverter_yield_fc2',
    'vrm_yield_today_fc2'
];

// helper function, normalizes data and sets timestamp as key
const normalizeWithTimestampAsKey = (entries) => entries.reduce(
    (rx, [timestamp, ...values]) => {
        rx[timestamp] = values;
        return rx;
    },
    {}
);

const generateDcMeterPowerField = () => {
    const key = `DcMeterPower`;
    return {
        [key] : {
            fieldOptions: {
                yAxis: 1
            },
            meta: {
                code: key,
                description: "DC Meter power",
                formatValueOnly: "%.1F",
                formatWithUnit: "%.1F W"
            },
            getData: (graphData, graphMeta) => {
                const idCurrent = _.findKey(
                    graphMeta,
                    instance => instance.code === 'dlI'
                );
                const idVoltage = _.findKey(
                    graphMeta,
                    instance => instance.code === 'dlV'
                );

                if (!idCurrent || !idVoltage) {
                    return [];
                }

                const CurrentTimestampNormalized = normalizeWithTimestampAsKey(graphData[idCurrent]);
                const VoltageTimestampNormalized = normalizeWithTimestampAsKey(graphData[idVoltage]);

                const calculatedEntries = Object.keys(CurrentTimestampNormalized)
                    .map(timestamp => {
                        const CurrentValues = CurrentTimestampNormalized[timestamp];
                        const VoltageValues = VoltageTimestampNormalized[timestamp];

                        if (!VoltageValues) {
                            return null;
                        }
                        return [
                            timestamp,
                            ...CurrentValues.map(
                                (val, idx) => val * VoltageValues[idx]
                            )
                        ];
                    })
                    .filter(entry => !!entry); // filter null values

                return calculatedEntries;
            }
        }
    }
}

const generateAlternatorInputCurrentVirtualField = () => {
    const key = `AlternatorInputCurrent`;
    return {
        [key] : {
            fieldOptions: {
                yAxis: 1
            },
            meta: {
                code: key,
                description: "Input current",
                formatValueOnly: "%.2F",
                formatWithUnit: "%.2F A"
            },
            getData: (graphData, graphMeta) => {
                const idPower = _.findKey(
                    graphMeta,
                    instance => instance.code === 'aliP'
                );
                const idVoltage = _.findKey(
                    graphMeta,
                    instance => instance.code === 'aliV'
                );

                if (!idPower || !idVoltage) {
                    return [];
                }

                const PowerTimestampNormalized = normalizeWithTimestampAsKey(graphData[idPower]);
                const VoltageTimestampNormalized = normalizeWithTimestampAsKey(graphData[idVoltage]);

                const calculatedEntries = Object.keys(PowerTimestampNormalized)
                    .map(timestamp => {
                        const PowerValues = PowerTimestampNormalized[timestamp];
                        const VoltageValues = VoltageTimestampNormalized[timestamp];

                        if (!VoltageValues) {
                            return null;
                        }
                        return [
                            timestamp,
                            ...PowerValues.map(
                                (val, idx) => {
                                    if (val === null || VoltageValues[idx] === null) {
                                        return null;
                                    }
                                    return val / VoltageValues[idx];
                                }
                            )
                        ];
                    })
                    .filter(entry => entry[1] !== null); // filter null values

                return calculatedEntries;
            }
        }
    }
}

const generateAlternatorOutputPowerVirtualField = () => {
    const key = `AlternatorOutputPower`;
    return {
        [key] : {
            fieldOptions: {
                yAxis: 1
            },
            meta: {
                code: key,
                description: "Output power",
                formatValueOnly: "%.1F",
                formatWithUnit: "%.1F W"
            },
            getData: (graphData, graphMeta) => {
                const idCurrent = _.findKey(
                    graphMeta,
                    instance => instance.code === 'alI'
                );
                const idVoltage = _.findKey(
                    graphMeta,
                    instance => instance.code === 'alV'
                );

                if (!idCurrent || !idVoltage) {
                    return [];
                }

                const CurrentTimestampNormalized = normalizeWithTimestampAsKey(graphData[idCurrent]);
                const VoltageTimestampNormalized = normalizeWithTimestampAsKey(graphData[idVoltage]);

                const calculatedEntries = Object.keys(CurrentTimestampNormalized)
                    .map(timestamp => {
                        const CurrentValues = CurrentTimestampNormalized[timestamp];
                        const VoltageValues = VoltageTimestampNormalized[timestamp];

                        if (!VoltageValues) {
                            return null;
                        }
                        return [
                            timestamp,
                            ...CurrentValues.map(
                                (val, idx) => val * VoltageValues[idx]
                            )
                        ];
                    })
                    .filter(entry => entry[1] !== null); // filter null values

                return calculatedEntries;
            }
        }
    }
}

const generateDcDcConverterInputCurrentVirtualField = () => {
    const key = `DcDcConverterInputCurrent`;
    return {
        [key] : {
            fieldOptions: {
                yAxis: 1
            },
            meta: {
                code: key,
                description: "Input current",
                formatValueOnly: "%.2F",
                formatWithUnit: "%.2F A"
            },
            getData: (graphData, graphMeta) => {
                const idPower = _.findKey(
                    graphMeta,
                    instance => instance.code === 'DciP'
                );
                const idVoltage = _.findKey(
                    graphMeta,
                    instance => instance.code === 'DciV'
                );

                if (!idPower || !idVoltage) {
                    return [];
                }

                const PowerTimestampNormalized = normalizeWithTimestampAsKey(graphData[idPower]);
                const VoltageTimestampNormalized = normalizeWithTimestampAsKey(graphData[idVoltage]);

                const calculatedEntries = Object.keys(PowerTimestampNormalized)
                    .map(timestamp => {
                        const PowerValues = PowerTimestampNormalized[timestamp];
                        const VoltageValues = VoltageTimestampNormalized[timestamp];

                        if (!VoltageValues) {
                            return null;
                        }
                        return [
                            timestamp,
                            ...PowerValues.map(
                                (val, idx) => {
                                    if (val === null || VoltageValues[idx] === null) {
                                        return null;
                                    }
                                    return val / VoltageValues[idx];
                                }
                            )
                        ];
                    })
                    .filter(entry => entry[1] !== null); // filter null values

                return calculatedEntries;
            }
        }
    }
}

const generateDcDcConverterOutputPowerVirtualField = () => {
    const key = `DcDcConverterOutputPower`;
    return {
        [key] : {
            fieldOptions: {
                yAxis: 1
            },
            meta: {
                code: key,
                description: "Output power",
                formatValueOnly: "%.1F",
                formatWithUnit: "%.1F W"
            },
            getData: (graphData, graphMeta) => {
                const idCurrent = _.findKey(
                    graphMeta,
                    instance => instance.code === 'DcoI'
                );
                const idVoltage = _.findKey(
                    graphMeta,
                    instance => instance.code === 'DcoV'
                );

                if (!idCurrent || !idVoltage) {
                    return [];
                }

                const CurrentTimestampNormalized = normalizeWithTimestampAsKey(graphData[idCurrent]);
                const VoltageTimestampNormalized = normalizeWithTimestampAsKey(graphData[idVoltage]);

                const calculatedEntries = Object.keys(CurrentTimestampNormalized)
                    .map(timestamp => {
                        const CurrentValues = CurrentTimestampNormalized[timestamp];
                        const VoltageValues = VoltageTimestampNormalized[timestamp];

                        if (!VoltageValues) {
                            return null;
                        }
                        return [
                            timestamp,
                            ...CurrentValues.map(
                                (val, idx) => val * VoltageValues[idx]
                            )
                        ];
                    })
                    .filter(entry => entry[1] !== null); // filter null values

                return calculatedEntries;
            }
        }
    }
}


const generatePVIVirtualField = (index = "") => {
    const key = `VirtualPVI${index}`;
    return {
        [key]: {
            fieldOptions: {
                yAxis: 1
            },
            meta: {
                code: key,
                description: "PV current",
                formatValueOnly: "%.1F",
                formatWithUnit: "%.1F A"
            },
            /**
             * We first calculate the supposed PVI from PVP and PVV  -> PVI = PVP divided by PVV
             * but there might be cases where old systems don't have PVP and still sends PVI
             * in those cases we merge (union) the calculated stuff with the recieved PVI from api
             * thus we cover edge cases such as an system that was upgraded and sends both PVI for a period of time
             * and PVP for another period of time
             */
            getData: (graphData, graphMeta) => {
                const idPVP = _.findKey(
                    graphMeta,
                    instance => instance.code === `PVP${index}`
                );
                const idPVV = _.findKey(
                    graphMeta,
                    instance => instance.code === `PVV${index}`
                );
                const idPVI = _.findKey(
                    graphMeta,
                    instance => instance.code === `PVI${index}`
                )

                if (!idPVP || !idPVV || !idPVI) {
                    return [];
                }

                const PVPTimestampNormalized = normalizeWithTimestampAsKey(graphData[idPVP]);
                const PVVTimestampNormalized = normalizeWithTimestampAsKey(graphData[idPVV]);

                const calculatedEntries = Object.keys(PVPTimestampNormalized)
                    .map(timestamp => {
                        const PVPValues = PVPTimestampNormalized[timestamp];
                        const PVVValues = PVVTimestampNormalized[timestamp];
                        if (!PVVValues) {
                            return null;
                        }
                        return [
                            timestamp,
                            ...PVPValues.map(
                                (val, idx) => {
                                    if (val === null || PVVValues[idx] === null) {
                                        return null;
                                    }
                                    return val / PVVValues[idx];
                                }
                            ) /* PVP divided by PVV */
                        ];
                    })
                    .filter(entry => !!entry); // filter null values

                return _.unionWith(calculatedEntries, graphData[idPVI], ([calculatedTimestamp], [pviTimestamp]) => {
                    return +calculatedTimestamp === +pviTimestamp;
                })
            }
        }
    };
};

export const generateConfigBasedOfSeries = (meta, series) => {
    meta.extraOptions.yAxis.title = { text: undefined };
    meta.extraOptions.yAxis.labels = { padding: 0 };

    const yColorsFromSeries = {};
    var yAxisIndex = 0;

    Object.values(series).forEach(serie => {
        const { code: dataAttribute, serieColor, instance, yAxisMax, yAxisMin, yAxisInterval } = serie;
        const config = mapCustomWidgetDataAttributeToYAxis(dataAttribute);
        // Use columns for showing energy (KWh)
        const serieOptions = mapCustomWidgetDataAttributeToSerieOptions(dataAttribute);
        if(yAxisMin && yAxisMax) {
            config.minTickInterval = 0.1;
            config.min = yAxisMin;
            config.max = yAxisMax;
            config.tickAmount = yAxisInterval;
        }
        yColorsFromSeries[yAxisIndex] = serieColor;
        meta.fields[dataAttribute + '_' + instance] = { yAxis: yAxisIndex };
        Object.assign(meta.fields[dataAttribute + '_' + instance], serieOptions);
        meta.extraOptions.yAxis.splice(yAxisIndex++, 0, config);
    });

    return { meta, yColorsFromSeries };
};

/**
 * Change series type to column for forecast data
 * @param dataAttribute Used to check if the current series instance needs to be plotted with columns
 * @returns {{serieOptions: {borderColor: string, shadow: boolean, borderWidth: number, pointPadding: number, type: string, groupPadding: number}}|{}}
 * Specific options to plot this series instance with columns.
 */
const mapCustomWidgetDataAttributeToSerieOptions = (dataAttribute) => {
    if (columnDataAttributes.includes(dataAttribute)) {
        return {
            serieOptions: {
                type: 'column',
                borderWidth: 0,
                // Display columns under lines
                zIndex: -1,
            }
        };
    } else {
        return {};
    }
}

const mapCustomWidgetDataAttributeToYAxis = (dataAttribute) => {
    switch (dataAttribute) {
        case 'IF1': case 'OF':
            return frequencyConfig;
        case 'IP1': case 'IP2': case 'IP3': case 'OP1': case 'OP2':
        case 'OP3': case 'i1': case 'o1': case 'coP': case 'pP1':
        case 'pP2': case 'pP3': case 'bP': case 'a1': case 'a2':
        case 'a3': case 'gs1': case 'gs2': case 'gs3': case 'g1':
        case 'g2': case 'g3': case 'P': case 'P2': case 'P3':
        case 'Pi': case 'Pi2': case 'Pi3': case 'Pdc': case 'cPo':
        case 'fcp':
            return powerConfig;
        case 'ScV': case 'PVV': case 'PVV0': case 'PVV1': case 'PVV2':
        case 'PVV3': case 'CV': case 'dc': case 'mvc': case 'c0V':
        case 'c1V': case 'c2V': case 'fcV':
            return voltageWithoutMinConfig;
        case 'ScI': case 'CI': case 'c0I': case 'c1I': case 'c2I':
        case 'fcI':
            return current10TicksConfig;
        case 'SLI': case 'iOI1': case 'II1': case 'II2': case 'II3':
        case 'OI1': case 'OI2': case 'OI3': case 'mcc': case 'mdc':
            return currentConfig;
        case 'iOV1': case 'IV1': case 'IV2': case 'IV3': case 'OV1':
        case 'OV2': case 'OV3':
            return voltageConfig;
        case 'SOC': case 'bs': case 'VSOC':
            return socConfig;
        case 'BT': case 'glT': case 'ScT': case 'CT': case 'T1':
        case 'mt': case 'coT': case 'tr':
            return temperatureConfig;
        case 'V': case 'bv': case 'VS': case 'VM':
            return batteryVoltageConfig;
        case 'I': case 'bc':
            return batteryCurrentConfig;
        case 'mr':
            return rpmConfig;
        case 'ScW':
            return solarPowerPvYield;
        case 'pcR':
            return dbConfig;
        case 'fcgf1': case 'fcgf2':
            return flowConfig;
        default:
            return customWidgetConfig;
    }
};

angular.module('vrm.components')
    .service('AdvancedGraphWidgetConfigs', /* @ngInject */  (UserService) => ({
    'AcInputFrequency': [{
        extraOptions: {
            yAxis: [frequencyConfig]
        },

        fields: {
            'IF1': null
        }
    }],
    'AcInputPower': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'IP1': null,
            'IP2': null,
            'IP3': null,
            'TOTAL': null
        },
        allowSum: true
    }],
    'SolarChargerVoltageAndCurrent': [{
        extraOptions: {
            yAxis: [voltageWithoutMinConfig, current10TicksConfig]
        },
        fields: {
            'ScV': null,
            'ScI': {
                yAxis: 1
            }
        }
    }],
    'SolarChargerPVVoltageAndCurrent': [{
        extraOptions: {
            yAxis: [voltageWithoutMinConfig, current10TicksConfig]
        },
        fields: {
            'PVV': null,
        },
        virtualFields: {
            ...generatePVIVirtualField(),
        },
        // fields to be retrieved by the api but not to be shown in the graph
        extraFields: ['PVP', 'PVI'],
    }],
    'SolarChargerPVVoltageAndCurrentTracker1': [{
        extraOptions: {
            yAxis: [voltageWithoutMinConfig, current10TicksConfig]
        },
        fields: {
            'PVV0': null,
        },
        virtualFields: {
            ...generatePVIVirtualField('0'),
        },
        extraFields: ['PVP0', 'PVI0'],
    }],
    'SolarChargerPVVoltageAndCurrentTracker2': [{
        extraOptions: {
            yAxis: [voltageWithoutMinConfig, current10TicksConfig]
        },
        fields: {
            'PVV1': null,
        },
        virtualFields: {
            ...generatePVIVirtualField('1'),
        },
        extraFields: ['PVP1', 'PVI1'],
    }],
    'SolarChargerPVVoltageAndCurrentTracker3': [{
        extraOptions: {
            yAxis: [voltageWithoutMinConfig, current10TicksConfig]
        },
        fields: {
            'PVV2': null,
        },
        virtualFields: {
            ...generatePVIVirtualField('2'),
        },
        extraFields: ['PVP2', 'PVI2'],
    }],
    'SolarChargerPVVoltageAndCurrentTracker4': [{
        extraOptions: {
            yAxis: [voltageWithoutMinConfig, current10TicksConfig]
        },
        fields: {
            'PVV3': null,
        },
        virtualFields: {
            ...generatePVIVirtualField('3'),
        },
        extraFields: ['PVP3', 'PVI3'],
    }],
    'SolarChargerLoadoutputCurrent': [{
        extraOptions: {
            yAxis: [currentConfig]
        },
        fields: {
            'SLI': null
        }
    }],
    'InverterOutputVoltageAndCurrent': [{
        extraOptions: {
            yAxis: [voltageConfig, currentConfig]
        },
        fields: {
            'iOV1': null,
            'iOI1': {
                yAxis: 1
            }
        },
    }],
    'AcInputVoltageAndCurrent': [
        {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'IV1': null,
                'II1': {
                    yAxis: 1
                }
            },
            'name': 'Phase 1'
        }, {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'IV2': null,
                'II2': {
                    yAxis: 1
                }
            },
            'name': 'Phase 2'
        }, {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'IV3': null,
                'II3': {
                    yAxis: 1
                }
            },
            'name': 'Phase 3'
        }
    ],
    'AcOutputFrequency': [{
        extraOptions: {
            yAxis: [frequencyConfig]
        },
        fields: {
            'OF': null
        }
    }],
    'AcOutputPower': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'OP1': null,
            'OP2': null,
            'OP3': null,
            'TOTAL': null
        },
        allowSum: true
    }],
    'AcOutputVoltageAndCurrent': [
        {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'OV1': null,
                'OI1': {
                    yAxis: 1
                }
            },
            'name': 'Phase 1'
        }, {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'OV2': null,
                'OI2': {
                    yAxis: 1
                }
            },
            'name': 'Phase 2'
        }, {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'OV3': null,
                'OI3': {
                    yAxis: 1
                }
            },
            'name': 'Phase 3'
        }
    ],
    'BatterySoc': [{
        extraOptions: {yAxis: [socConfig]},
        fields: {
            'SOC': null
        }
    }],
    'SystemBatterySoc': [{
        extraOptions: {yAxis: [socConfig]},
        fields: {
            'bs': null
        }
    }],
    "SystemCriticalLoads": [{
        extraOptions: {yAxis: [powerConfig]},
        fields: {
            'i1': null,
            'o1': null,
        },
    }],
    'BatteryTemperature': [{
        extraOptions: {
            yAxis: [temperatureConfig]
        },
        fields: {
            'BT': null
        }
    }],
    'InternalTemperature': [{
        extraOptions: {
            yAxis: [temperatureConfig]
        },
        fields: {
            'glT': null
        }
    }],
    'BatteryTemperatureSolarCharger': [{
        extraOptions: {
            yAxis: [temperatureConfig]
        },
        fields: {
            'ScT': null
        }
    }],
    'BatteryCellTemperatureMinMax': [{
        extraOptions: {
            yAxis: [temperatureConfig]
        },
        fields: {
            'mcT': null,
            'McT': null
        }
    }],
    'BatteryTemperatureVeBus': [{
        extraOptions: {
            yAxis: [temperatureConfig]
        },
        fields: {
            'CT': null
        }
    }],
    'BatteryVoltageAndCurrent': [{
        extraOptions: {
            yAxis: [batteryVoltageConfig, batteryCurrentConfig]
        },
        fields: {
            'V': null,
            'I': {
                yAxis: 1
            }
        }
    }],
    'SystemBatteryVoltageAndCurrent': [{
        extraOptions: {
            yAxis: [batteryVoltageConfig, batteryCurrentConfig]
        },
        fields: {
            'bv': null,
            'bc': {
                yAxis: 1
            }
        }
    }],
    'DcVoltageAndCurrent': [{
        extraOptions: {
            yAxis: [voltageWithoutMinConfig, current10TicksConfig]
        },
        fields: {
            'CV': null,
            'CI': {
                yAxis: 1
            }
        }
    }],
    'IOExtenderTemperature': [{
        extraOptions: {
            yAxis: [temperatureConfig]
        },
        fields: {
            'T1': null
        }
    }],
    'MotorAndControllerTemperature': [{
        extraOptions: {
            yAxis: [temperatureConfig, temperatureConfig]
        },
        fields: {
            'mt': null,
            'coT': {
                yAxis: 1
            }
        }
    }],
    'MotorRPMAndControllerPower': [{
        extraOptions: {
            yAxis: [rpmConfig, powerConfig]
        },
        fields: {
            'mr': null,
            'coP': {
                yAxis: 1
            }
        }
    }],
    'PVInverterYield': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'pP1': null,
            'pP2': null,
            'pP3': null,
            'TOTAL': null
        },
        allowSum: true
    }],
    'SolarChargerPVYield': [{
        extraOptions: {
            yAxis: [solarPowerPvYield]
        },
        fields: {
            'ScW': null
        }
    }],
    'SolarIrradiance': [{
        extraOptions: {
            margin: 60,
            yAxis: [solarIrradiance],
        },
        fields: {
            'vrm_solar_irradiance': null,
            'vrm_solar_irradiance_fc': null
        }
    }],
    'StarterBatteryVoltage': [{
        extraOptions: {
            yAxis: [batteryVoltageConfig]
        },
        fields: {
            'VS': null
        }
    }],
    'SystemBatteryPower': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'bp': null
        }
    }],
    'SystemAcConsumption': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'a1': null,
            'a2': null,
            'a3': null,
            'TOTAL': null
        },
        allowSum: true
    }],
    'SystemAcGenset': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'gs1': null,
            'gs2': null,
            'gs3': null,
            'TOTAL': null
        },
        allowSum: true
    }],
    'SignalRSSI': [{
        extraOptions: {
            yAxis: [dbConfig]
        },
        fields: {
            'pcR': null,
        },
        allowSum: false
    }],
    'SystemGrid': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'g1': null,
            'g2': null,
            'g3': null,
            'TOTAL': null
        },
        allowSum: true
    }],
    'VeBusSoc': [{
        extraOptions: {yAxis: [socConfig]},
        fields: {
            'VSOC': null
        }
    }],
    'SystemDcSystem': [{
        extraOptions: {yAxis: [voltageWithoutMinConfig]},
        fields: {
            'dc': null
        }
    }],
    'SystemPVYield': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'P': null,
            'P2': null,
            'P3': null,
            'Pi': null,
            'Pi2': null,
            'Pi3': null,
            'Pdc': null,
            'TOTAL': null
        },
        allowSum: true,
        totalOnly: true
    }],
    'BMSChargeAndDischargeLimits': [{
        extraOptions: {
            yAxis: [voltageWithoutMinConfig, currentConfig]
        },
        fields: {
            'mvc': null,
            'mcc': {
                yAxis: 1
            },
            'mdc': {
                yAxis: 1
            },
        },
        allowSum: false,
        totalOnly: false
    }],
    'TankLevel': [{
        extraOptions: {
            yAxis: [temperatureConfig]
        },
        fields: {
            'tr': null,
        },
        preprocessMeta: function (meta) {
            if (meta && meta[0]) {
                if (UserService.user.useGallons) {
                    meta[0].formatWithUnit = '%.2F Gal';
                    meta[0].formatValueOnly = '%.2F';
                }
                else {
                    meta[0].formatWithUnit = '%.0F L';
                    meta[0].formatValueOnly = '%.0F';
                }
            }
            return meta;

        },
        preprocessValue: function (value) {
            if (value === null) {
                return null;
            } else if (value > 1000) {
                return Math.round(parseFloat(value));
            } else {
                return Math.round(parseFloat(value) * 100) / 100;
            }
        }
    }],
    'BatteryMidVoltage': [{
        preprocessMeta: function (meta) {
            _.each(meta, function (singleMeta, key) {
                if (singleMeta.code === 'V') {
                    meta[key].description = 'Top voltage';
                }

                if (singleMeta.code === 'VM') {
                    meta[key].description = 'Bottom voltage';
                }

                if (singleMeta.code === 'VMD') {
                    meta[key].description = 'Mid-point deviation';
                }
            });
            return meta;

        },
        extraOptions: {
            yAxis: [batteryVoltageConfig, percentageConfig]
        },
        getColors: function (GraphColors) {
            return [GraphColors.blue, GraphColors.orange, GraphColors.black]
        },
        fields: {
            'V': null,
            'VM': null,
            'VMD': {
                yAxis: 1
            },
        },
        preprocessValue: function (originalValue, timestamp, attribute, data, meta) {
            let value = originalValue;
            // If we have a 'V' (voltage) value, substract the 'VM' (midpoint voltage) value from it at that point in time to get the top voltage
            if (originalValue !== null && attribute === 'V') {
                _.find(data, function (row, idDataAttribute) {
                    if (meta[idDataAttribute].code === 'VM') {
                        _.find(row, function (val) {
                            if (val[0] === timestamp) {
                                value = originalValue - val[1];
                                return true;
                            }
                        });
                        return true;
                    }
                });
            }
            return value;
        }
    }],
    'ChargerAcCurrent': [
        {
            extraOptions: {
                yAxis: [current10TicksConfig]
            },
            fields: {
                'cI': null
            }
        }
    ],
    'ChargerAcPower': [
        {
            extraOptions: {
                yAxis: [powerConfig]
            },
            fields: {
                'cPo': null
            }
        }
    ],
    'ChargerOutputVoltageAndCurrent': [
        {
            extraOptions: {
                yAxis: [voltageWithoutMinConfig, current10TicksConfig]
            },
            fields: {
                'c0V': null,
                'c0I': {
                    yAxis: 1
                }
            },
            'name': 'Output 1'
        }, {
            extraOptions: {
                yAxis: [voltageWithoutMinConfig, current10TicksConfig]
            },
            fields: {
                'c1V': null,
                'c1I': {
                    yAxis: 1
                }
            },
            'name': 'Output 2'
        }, {
            extraOptions: {
                yAxis: [voltageWithoutMinConfig, current10TicksConfig]
            },
            fields: {
                'c2V': null,
                'c2I': {
                    yAxis: 1
                }
            },
            'name': 'Output 3'
        }
    ],
    'FuelCellTemperatures': [
        {
            extraOptions: {
                yAxis: [temperatureConfigWithoutMin]
            },
            fields: {
                'fcab1': null,
                'fcab2': null
            },
            'name': 'Afterburner'
        }, {
            extraOptions: {
                yAxis: [temperatureConfigWithoutMin]
            },
            fields: {
                'fcst1': null,
                'fcst2': null,
                'fcst3': null,
                'fcst4': null
            },
            'name': 'Stack'
        }, {
            extraOptions: {
                yAxis: [temperatureConfigWithoutMin]
            },
            fields: {
                'fcrt1': null,
                'fcrt2': null,
            },
            'name': 'Reformer'
        }
    ],
    'FuelCellFlows': [{
        extraOptions: {
            yAxis: [flowConfig]
        },
        fields: {
            'fcgf1': null,
            'fcgf2': null,
        }
    }],
    'FuelCellPower': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'fcp': null,
        }
    }],
    'FuelCellBusStatus': [{
        extraOptions: {
            yAxis: [voltageWithoutMinConfig, current10TicksConfig]
        },
        fields: {
            'fcV': null,
            'fcI': {
                yAxis: 1
            }
        }
    }],
    'PulseMeterAggregate': [{
        fields: {
            'ma': null,
        },
    }],
    'BatteryCellMinMax': [{
        extraOptions: {
            yAxis: [batteryCellVoltageConfig],
        },
        fields: {
            'McV': null,
            'mcV': null,
        },
        getColors: function (GraphColors) {
            return [GraphColors.orange, GraphColors.blue]
        }
    }],
    'BatteryConsumedAmphours': [{
        fields: {
            'CE': null,
        },
    }],
    'EvChargerPower': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'evp': null
        }
    }],
    'AlternatorTemperature': [{
        fields: {
            'alT': null,
        },
    }],
    'AlternatorVoltage': [{
        fields: {
            'alV': null,
        },
    }],
    'AlternatorCurrent': [{
        fields: {
            'alI': null
        },
    }],
    'AlternatorSpeedAndDrive': [{
        extraOptions: {
            yAxis: [rpmConfig, percentageConfig],
        },
        fields: {
            'ales': null,
            'alas': null,
            'alfd': {
                yAxis: 1,
            },
        },
    }],
    'AlternatorInputVoltageAndCurrent': [{
        extraOptions: {
            yAxis: [voltageConfig, currentConfig]
        },
        fields: {
            'aliV': null,
        },
        virtualFields: {
            ...generateAlternatorInputCurrentVirtualField(),
        },
        extraFields: ['aliP']
    }],
    'AlternatorOutputVoltageAndCurrent': [{
        extraOptions: {
            yAxis: [voltageConfig, currentConfig]
        },
        fields: {
            'alV': null,
            'alI': {
                yAxis: 1
            }
        }
    }],
    'AlternatorInputPower': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'aliP': null
        }
    }],
    'AlternatorOutputPower': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        virtualFields: {
            ...generateAlternatorOutputPowerVirtualField(),
        },
        extraFields: ['alI', 'alV']
    }],
    'DcLoadCurrent': [{
        fields: {
            'dlI': null
        },
    }],
    'DcLoadPower': [{
        fields: {
            'virtual_field_DcMeterPower': null
        },
        virtualFields: {
            ...generateDcMeterPowerField(),
        },
        extraFields: ['dlI', 'dlV']
    }],
    'AcMeterPower': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'a1p': null,
            'a2p': null,
            'a3p': null,
            'TOTAL': null
        },
        allowSum: true
    }],
    'GridMeterPower': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'g1p': null,
            'g2p': null,
            'g3p': null,
            'TOTAL': null
        },
        allowSum: true
    }],
    'InverterChargerPVPower': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'mPVP': null,
        },
    }],
    'InverterChargerPVVoltage': [{
        extraOptions: {
            yAxis: [voltageConfig]
        },
        fields: {
            'mPVV': null,
        },
    }],
    'InverterChargerBatteryVoltage': [{
        extraOptions: {
            yAxis: [batteryVoltageConfig]
        },
        fields: {
            'mV': null,
        },
    }],
    'InverterChargerBatteryVoltageAndCurrent': [{
        extraOptions: {
            yAxis: [batteryVoltageConfig, currentConfig]
        },
        fields: {
            'mV': null,
            'mI': {
                yAxis: 1
            }
        }
    }],
    'InverterChargerDCCurrent': [{
        extraOptions: {
            yAxis: [currentConfig]
        },
        fields: {
            'mI': null,
        },
    }],
    'InverterChargerACOutputVoltageAndCurrent': [
        {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'mOV1': null,
                'mOI1': {
                    yAxis: 1
                }
            },
            'name': 'Phase 1'
        }, {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'mOV2': null,
                'mOI2': {
                    yAxis: 1
                }
            },
            'name': 'Phase 2'
        }, {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'mOV3': null,
                'mOI3': {
                    yAxis: 1
                }
            },
            'name': 'Phase 3'
        }
    ],
    'InverterChargerACInputVoltageAndCurrent': [
        {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'mIV1': null,
                'mII1': {
                    yAxis: 1
                }
            },
            'name': 'Phase 1'
        }, {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'mIV2': null,
                'mII2': {
                    yAxis: 1
                }
            },
            'name': 'Phase 2'
        }, {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'mIV3': null,
                'mII3': {
                    yAxis: 1
                }
            },
            'name': 'Phase 3'
        }
    ],
    'InverterChargerAcOutputPower': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'mOP1': null,
            'mOP2': null,
            'mOP3': null,
            'TOTAL': null
        },
        allowSum: true
    }],
    'InverterChargerAcOutputFrequency': [{
        extraOptions: {
            yAxis: [frequencyConfig]
        },
        fields: {
            'mOF1': null
        }
    }],
    'InverterChargerAcInputPower': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'mIP1': null,
            'mIP2': null,
            'mIP3': null,
            'TOTAL': null
        },
        allowSum: true
    }],
    'InverterChargerAcInputFrequency': [{
        extraOptions: {
            yAxis: [frequencyConfig]
        },

        fields: {
            'mIF1': null
        }
    }],
    'InverterChargerBatterySoc': [{
        extraOptions: {yAxis: [socConfig]},
        fields: {
            'mSOC': null
        }
    }],
    'InverterRSSolarPVPower': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'iPVP': null,
        },
    }],
    'InverterRSSolarPVVoltage': [{
        extraOptions: {
            yAxis: [voltageConfig]
        },
        fields: {
            'iPVV': null,
        },
    }],
    'InverterRSSolarBatteryVoltage': [{
        extraOptions: {
            yAxis: [batteryVoltageConfig]
        },
        fields: {
            'i0V': null,
        },
    }],
    'InverterRSSolarDCCurrent': [{
        extraOptions: {
            yAxis: [currentConfig]
        },
        fields: {
            'il': null,
        },
    }],
    'InverterRSSolarACOutputVoltageAndCurrent': [{
        extraOptions: {
            yAxis: [voltageConfig, currentConfig]
        },
        fields: {
            'iOV1': null,
            'iOI1': {
                yAxis: 1
            }
        }
    }],
    'InverterRSSolarAcOutputPower': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'iOP1': null
        }
    }],
    'InverterRSSolarAcOutputFrequency': [{
        extraOptions: {
            yAxis: [frequencyConfig]
        },
        fields: {
            'iOF1': null
        }
    }],
    'DcDcConverterInputVoltage': [{
        extraOptions: {yAxis: [voltageConfig]},
        fields: {
            'DciV': null
        }
    }],
    'DcDcConverterOutputVoltage': [{
        extraOptions: {yAxis: [voltageConfig]},
        fields: {
            'DcoV': null
        }
    }],
    'DcDcConverterInputVoltageAndCurrent': [{
        extraOptions: {
            yAxis: [voltageConfig, currentConfig]
        },
        fields: {
            'DciV': null,
        },
        virtualFields: {
            ...generateDcDcConverterInputCurrentVirtualField(),
        },
        extraFields: ['DciP']
    }],
    'DcDcConverterOutputVoltageAndCurrent': [{
        extraOptions: {
            yAxis: [voltageConfig, currentConfig]
        },
        fields: {
            'DcoV': null,
            'DcoI': {
                yAxis: 1
            }
        }
    }],
    'DcDcConverterInputPower': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'DciP': null
        }
    }],
    'DcDcConverterOutputPower': [{
        extraOptions: {
            yAxis: [powerConfig]
        },
        virtualFields: {
            ...generateDcDcConverterOutputPowerVirtualField(),
        },
        extraFields: ['DcoI', 'DcoV']
    }],
    'SmartLithiumCellVoltages': [
        {
            extraOptions: {yAxis: [batteryCellVoltageConfig]},
            fields: {
                'sLcV1': null,
                'sLcV2': null,
                'sLcV3': null,
                'sLcV4': null
            },
            name: 'Cells 1-4'
        }, {
            extraOptions: {yAxis: [batteryCellVoltageConfig]},
            fields: {
                'sLcV5': null,
                'sLcV6': null,
                'sLcV7': null,
                'sLcV8': null
            },
            name: 'Cells 5-8'
        }
    ],
    'SmartLithiumBatteryVoltage': [{
        extraOptions: {yAxis: [batteryVoltageConfig]},
        fields: {
            'sLV': null
        }
    }],
    'SmartLithiumBatteryTemperature': [{
        extraOptions: {yAxis: [temperatureConfig]},
        fields: {
            'sLT': null
        }
    }],
    'SmartBatteryProtectInputVoltage': [{
        extraOptions: {yAxis: [voltageConfig]},
        fields: {
            'bPiV': null
        }
    }],
    'SmartBatteryProtectOutputVoltage': [{
        extraOptions: {yAxis: [voltageConfig]},
        fields: {
            'bPoV': null
        }
    }],
    'GridMeterVoltageAndCurrent': [
        {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'g1v': null,
                'g1c': {
                    yAxis: 1
                }
            },
            'name': 'Phase 1'
        }, {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'g2v': null,
                'g2c': {
                    yAxis: 1
                }
            },
            'name': 'Phase 2'
        }, {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'g3v': null,
                'g3c': {
                    yAxis: 1
                }
            },
            'name': 'Phase 3'
        }
    ],
    'GridMeterCombined': [
        {
            extraOptions: {
                yAxis: [currentConfig]
            },
            fields: {
                'g1c': null,
                'g2c': null,
                'g3c': null,
            },
            'name': 'Current'
        }, {
            extraOptions: {
                yAxis: [voltageConfig]
            },
            fields: {
                'g1v': null,
                'g2v': null,
                'g3v': null,
            },
            'name': 'Voltage'
        }, {
            extraOptions: {
                yAxis: [powerConfig]
            },
            fields: {
                'g1p': null,
                'g2p': null,
                'g3p': null,
            },
            'name': 'Power'
    }],
    'PvInverterVoltageAndCurrent': [
        {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'pV1': null,
                'pI1': {
                    yAxis: 1
                }
            },
            'name': 'Phase 1'
        }, {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'pV2': null,
                'pI2': {
                    yAxis: 1
                }
            },
            'name': 'Phase 2'
        }, {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'pV3': null,
                'pI3': {
                    yAxis: 1
                }
            },
            'name': 'Phase 3'
        }
    ],
    'PvInverterCombined': [
    {
        extraOptions: {
            yAxis: [currentConfig]
        },
        fields: {
            'pI1': null,
            'pI2': null,
            'pI3': null,
        },
        'name': 'Current'
    }, {
        extraOptions: {
            yAxis: [voltageConfig]
        },
        fields: {
            'pV1': null,
            'pV2': null,
            'pV3': null,
        },
        'name': 'Voltage'
    }, {
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'pP1': null,
            'pP2': null,
            'pP3': null,
        },
        'name': 'Power'
    }],
    'AcMeterVoltageAndCurrent': [
        {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'a1v': null,
                'a1c': {
                    yAxis: 1
                }
            },
            'name': 'Phase 1'
        }, {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'a2v': null,
                'a2c': {
                    yAxis: 1
                }
            },
            'name': 'Phase 2'
        }, {
            extraOptions: {
                yAxis: [voltageConfig, currentConfig]
            },
            fields: {
                'a3v': null,
                'a3c': {
                    yAxis: 1
                }
            },
            'name': 'Phase 3'
        }
    ],
    'AcMeterCombined': [
    {
        extraOptions: {
            yAxis: [currentConfig]
        },
        fields: {
            'a1c': null,
            'a2c': null,
            'a3c': null,
        },
        'name': 'Current'
    }, {
        extraOptions: {
            yAxis: [voltageConfig]
        },
        fields: {
            'a1v': null,
            'a2v': null,
            'a3v': null,
        },
        'name': 'Voltage'
    }, {
        extraOptions: {
            yAxis: [powerConfig]
        },
        fields: {
            'a1p': null,
            'a2p': null,
            'a3p': null,
        },
        'name': 'Power'
    }],
    'CustomWidget': {
        extraOptions: {
            yAxis: [],
            title: {
                text: ''
            },
            subtitle: {
                text: ''
            },
            legend: {
                enabled: false
            },
        },
        fields: { },
        extraFields: [],
        getColors: function (GraphColors) {
            return [GraphColors.blue, GraphColors.red, GraphColors.orange, GraphColors.green, GraphColors.black];
        },
    }

}));
